import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNexusStore } from '../../stores/nexusStore'
import nexusApi from '../../services/nexusApi'
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react'

const NX = {
  bg: '#0D1117',
  surface: '#161B22',
  surface2: '#1C2330',
  border: '#2A3441',
  text: '#E6EDF3',
  textDim: '#8B949E',
  accent: '#0066FF',
  green: '#3DB54A',
  amber: '#F5921E',
  red: '#EF4444',
}

export default function NexusLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { setAuth } = useNexusStore()
  const navigate = useNavigate()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { data } = await nexusApi.post('/auth/login', { email, password })
      setAuth(data.token, data.admin)
      navigate('/nexus/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: NX.bg, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '2rem',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            width: 60, height: 60, borderRadius: 16, background: NX.accent,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '1rem',
          }}>
            <Shield size={32} color="#fff" />
          </div>
          <h1 style={{ color: NX.text, fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>NEXUS</h1>
          <p style={{ color: NX.textDim, margin: '0.375rem 0 0', fontSize: '0.875rem' }}>
            HSEQ Intelligence Operations Platform
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12,
          padding: '2rem',
        }}>
          <h2 style={{ color: NX.text, fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1.5rem' }}>
            Super Admin Login
          </h2>

          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.12)', border: `1px solid ${NX.red}`,
              borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              <AlertCircle size={16} color={NX.red} />
              <span style={{ color: NX.red, fontSize: '0.875rem' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: NX.textDim, fontSize: '0.8125rem', fontWeight: 500, display: 'block', marginBottom: '0.375rem' }}>
                Email Address
              </label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required autoFocus
                placeholder="admin@hseqintelligence.com.au"
                style={{
                  width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`,
                  borderRadius: 8, padding: '0.625rem 0.875rem', color: NX.text,
                  fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: NX.textDim, fontSize: '0.8125rem', fontWeight: 500, display: 'block', marginBottom: '0.375rem' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{
                    width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`,
                    borderRadius: 8, padding: '0.625rem 2.75rem 0.625rem 0.875rem', color: NX.text,
                    fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button" onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim, padding: 0,
                  }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', background: NX.accent, color: '#fff',
                border: 'none', borderRadius: 8, padding: '0.75rem',
                fontSize: '0.9375rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1, transition: 'opacity 0.15s',
              }}
            >
              {loading ? 'Signing in...' : 'Sign In to NEXUS'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', color: NX.textDim, fontSize: '0.75rem', marginTop: '1.5rem' }}>
          Restricted access. All actions are logged.
        </p>
      </div>
    </div>
  )
}
